<template>
  <span class="time-picker">
    <input
      :id="id"
      v-model="displayTime"
      class="display-time"
      type="text"
      readonly
      @click.stop="toggleDropdown">
    <span
      v-if="!hideClearButton"
      v-show="!showDropdown && showClearButton"
      class="clear-btn"
      @click.stop="clearTime">&times;</span>
    <div
      v-if="showDropdown"
      class="time-picker-overlay"
      @click.stop="toggleDropdown" />
    <div
      v-show="showDropdown"
      class="dropdown">
      <div class="select-list">
        <ul class="hours">
          <li
            class="hint"
            v-text="hourType" />
          <li
            v-for="(hr, index) in hours"
            :key="index"
            :class="{active: hour === hr}"
            @click.stop="select('hour', hr)"
            v-text="hr" />
        </ul>
        <ul class="minutes">
          <li
            class="hint"
            v-text="minuteType" />
          <li
            v-for="(m, index) in minutes"
            :key="index"
            :class="{active: minute === m}"
            @click.stop="select('minute', m)"
            v-text="m" />
        </ul>
        <ul
          v-if="secondType"
          class="seconds">
          <li
            class="hint"
            v-text="secondType" />
          <li
            v-for="(s, index) in seconds"
            :key="index"
            :class="{active: second === s}"
            @click.stop="select('second', s)"
            v-text="s" />
        </ul>
        <ul
          v-if="apmType"
          class="apms">
          <li
            class="hint"
            v-text="apmType" />
          <li
            v-for="(a, index) in apms"
            :key="index"
            :class="{active: apm === a}"
            @click.stop="select('apm', a)"
            v-text="a" />
        </ul>
      </div>
    </div>
  </span>
</template>

<script>
  // Originally forked from https://github.com/phoenixwong/vue2-timepicker

  const CONFIG = {
    HOUR_TOKENS: ['HH', 'H', 'hh', 'h', 'kk', 'k'],
    MINUTE_TOKENS: ['mm', 'm'],
    SECOND_TOKENS: ['ss', 's'],
    APM_TOKENS: ['A', 'a'],
  }

  export default {
    name: 'TimePicker',

    props: {
      time: {
        required: true,
        type: Object,
      },
      hideClearButton: {
        type: Boolean,
      },
      format: {
        type: String,
        default: 'HH:mm',
      },
      minuteInterval: {
        type: Number,
        default: 1,
        validator: val => {
          if (val < 0) {
            throw new Error('minute-interval must be greater than 0')
          }
          return true
        },
      },
      secondInterval: {
        type: Number,
        default: 1,
        validator: val => {
          if (val < 0) {
            throw new Error('second-interval must be greater than 0')
          }
          return true
        },
      },
      id: {
        type: String,
        default: 'time-picker',
      },
    },

    data() {
      return {
        hour: this.time.hour,
        minute: this.time.minute,
        second: this.time.second,
        apm: this.time.apm,
        showDropdown: false,
      }
    },

    computed: {
      hourType() {
        return this.checkAcceptingType(CONFIG.HOUR_TOKENS, this.format, 'HH')
      },
      minuteType() {
        return this.checkAcceptingType(CONFIG.MINUTE_TOKENS, this.format, 'mm')
      },
      secondType() {
        return this.checkAcceptingType(CONFIG.SECOND_TOKENS, this.format)
      },
      apmType() {
        return this.checkAcceptingType(CONFIG.APM_TOKENS, this.format)
      },
      displayTime() {
        let timeString = ''

        timeString += this.hourType ? (this.hour ? this.hour : this.hourType) : ''
        timeString += this.minuteType ? ':' + (this.minute ? this.minute : this.minuteType) : ''
        timeString += this.secondType ? ':' + (this.second ? this.second : this.secondType) : ''
        timeString += this.apmType ? ' ' + (this.apm ? this.apm : this.apmType) : ''

        this.$emit('update:time', {hour: this.hour, minute: this.minute, second: this.second, apm: this.apm})

        return timeString
      },
      showClearButton() {
        return this.displayTime !== this.format
      },
      hours() {
        const hoursCount = this.isTwelveHours() ? 12 : 24
        const hours = []

        for (let i = 0; i < hoursCount; i++) {
          hours.push(this.formatValue(this.hourType, i))
        }

        return hours
      },
      minutes() {
        return this.renderList(this.minuteType, this.minuteInterval)
      },
      seconds() {
        return this.renderList(this.secondType, this.secondInterval)
      },
      apms() {
        if (!this.apmType) {
          return []
        }
        return this.apmType === 'A' ? ['AM', 'PM'] : ['am', 'pm']
      },
    },

    watch: {
      time: function (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.hour = newVal.hour
          this.minute = newVal.minute
          this.second = newVal.second
          this.apm = newVal.apm
        }
      },
    },

    methods: {
      formatValue(type, i) {
        switch (type) {
          case 'H':
          case 'm':
          case 's':
            return String(i)
          case 'HH':
          case 'mm':
          case 'ss':
            return i < 10 ? `0${i}` : String(i)
          case 'h':
          case 'k':
            return String(i + 1)
          case 'hh':
          case 'kk':
            return (i + 1) < 10 ? `0${i + 1}` : String(i + 1)
          default:
            return ''
        }
      },

      checkAcceptingType(validValues, formatString, fallbackValue) {
        if (!validValues || !formatString || !formatString.length) {
          return ''
        }

        for (let i = 0; i < validValues.length; i++) {
          if (formatString.indexOf(validValues[i]) > -1) {
            return validValues[i]
          }
        }

        return fallbackValue || ''
      },

      renderList(listType, interval) {
        if (interval === 0) {
          interval = 60
        } else if (interval > 60) {
          console.warn('`' + listType + '-interval` should be less than 60. Current value is', interval)
          interval = 1
        } else if (interval < 1) {
          console.warn('`' + listType + '-interval` should be NO less than 1. Current value is', interval)
          interval = 1
        } else if (!interval) {
          interval = 1
        }

        const values = []

        for (let i = 0; i < 60; i += interval) {
          values.push(this.formatValue(listType, i))
        }

        return values
      },

      isTwelveHours(token) {
        return token === 'h' || token === 'hh'
      },

      toggleDropdown() {
        this.showDropdown = !this.showDropdown
      },

      select(type, value) {
        if (type === 'hour') {
          this.hour = value
        } else if (type === 'minute') {
          this.minute = value
        } else if (type === 'second') {
          this.second = value
        } else if (type === 'apm') {
          this.apm = value
        }
      },

      clearTime() {
        this.hour = null
        this.minute = null
        this.second = null
        this.apm = null
      },
    },
  }
</script>

<style>
  .time-picker {
    display: inline-block;
    position: relative;
    font-size: 1em;
    width: 10em;
    font-family: sans-serif;
    vertical-align: middle;
  }

  .time-picker * {
    box-sizing: border-box;
  }

  .time-picker input.display-time {
    border: 1px solid #d2d2d2;
    width: 10em;
    height: 2.2em;
    padding: 0.3em 0.5em;
    font-size: 1em;
  }

  .time-picker .clear-btn {
    position: absolute;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    top: 0;
    right: 0;
    bottom: 0;
    margin-top: -0.15em;
    z-index: 3;
    font-size: 1.1em;
    line-height: 1em;
    vertical-align: middle;
    width: 1.3em;
    color: #d2d2d2;
    background: rgba(255, 255, 255, 0);
    text-align: center;
    font-style: normal;

    -webkit-transition: color .2s;
    transition: color .2s;
  }

  .time-picker .clear-btn:hover {
    color: #797979;
    cursor: pointer;
  }

  .time-picker .time-picker-overlay {
    z-index: 2;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .time-picker .dropdown {
    position: absolute;
    z-index: 5;
    top: calc(2.2em + 2px);
    left: 0;
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
    width: 10em;
    height: 10em;
    font-weight: normal;
  }

  .time-picker .dropdown .select-list {
    width: 10em;
    height: 10em;
    overflow: hidden;
    display: flex;
    flex-flow: row nowrap;
    align-items: stretch;
    justify-content: space-between;
  }

  .time-picker .dropdown ul {
    padding: 0;
    margin: 0;
    list-style: none;

    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .time-picker .dropdown ul.minutes,
  .time-picker .dropdown ul.seconds,
  .time-picker .dropdown ul.apms {
    border-left: 1px solid #fff;
  }

  .time-picker .dropdown ul li {
    text-align: center;
    padding: 0.3em 0;
    color: #161616;
  }

  .time-picker .dropdown ul li:not(.hint):hover {
    background: rgba(0, 0, 0, .08);
    color: #161616;
    cursor: pointer;
  }

  .time-picker .dropdown ul li.active,
  .time-picker .dropdown ul li.active:hover {
    background: #41B883;
    color: #fff;
  }

  .time-picker .dropdown .hint {
    color: #a5a5a5;
    cursor: default;
    font-size: 0.8em;
  }
</style>
